import React, { useState, useEffect, useRef } from 'react';
import { Button, Icon, IconButton, Chip, TextField, InputAdornment } from '@mui/material';
import useStyles from './styles';
import { useScreen } from 'global/styles';
import Form from 'global/components/form';
import { get, size, first, set } from 'global/lib/lodash';
import { UploadFiles } from 'redux/actions/imageUploader';
import { FILEADD, VIDEOADD } from '../../definitions';
import sendData from 'redux/actions/sendData';
import Alert from '@mui/material/Alert';
import Confirm from 'global/components/confirm';
import CloseIcon from '@mui/icons-material/Close';
import { awaitAll } from 'global/utils/awaitAll';
import clsx from 'clsx';
import { _size } from 'global/utils';
import { useWindowSize } from 'global/hooks/useWindowSize';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { sortItemsAlphabetically } from 'global/utils';

const possiblevideos = ['.mp4', '.webm', '.ogg', { id: '.mov', mime: 'quicktime' }];
const isVideoFile = (file) => !!possiblevideos.find((v) => get(file, 'fileName', '').indexOf(v.id ? v.id : v) > -1);
const stopProp = (e) => {
  e.stopPropagation();
  e.preventDefault();
};

const RenderFile = (props) => {
  const { state, onDocChange } = props;
  const document = get(props, 'document', {});
  const file = get(document, 'file', {}) || {};
  const [doc, setDoc] = useState({ ...document });
  const ref = useRef();

  const classes = useStyles();

  const isVideo = isVideoFile(file);

  useWindowSize();

  const onDelete = () => {
    get(props, 'onDeleteFile', () => 0)(props);
  };

  const onChange = (e) => {
    set(doc, `name`, e.target.value);
    setDoc({ ...doc });
    onDocChange(doc);
  };

  const url = get(file, 'fullUrl', get(document, 'url'));

  const name = (() => {
    const obj = {
      name: document.name || get(file, 'name', get(document, 'url', '')),
      original: get(document, 'file.name', get(document, 'url')),
    };

    const isLink = get(document, 'url');

    if (isLink && isLink !== obj.name) {
      obj.name = `${obj.name} (${isLink})`;
    }
    return obj;
  })();

  if (state.isEdit) {
    return (
      <div className={clsx(classes.fileWrap, classes.fileMargin)}>
        <TextField
          value={get(doc, 'name')}
          onChange={onChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={onDelete} size="large">
                  <Icon>delete</Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <div style={{ fontSize: 11 }}>{name.original}</div>
      </div>
    );
  }

  if (isVideo) {
    const width = get(ref, 'current.clientWidth', 320);
    const dims = {
      width,
      height: width * 0.75,
    };

    return (
      <div ref={ref} className={clsx(classes.fileWrap, classes.fileMargin)}>
        <div>{name.name}</div>
        <video {...dims} controls src={get(file, 'url')} />
        {state.isEdit && (
          <IconButton
            variant="contained"
            className={classes.videoDelete}
            onClick={onDelete}
            size="large">
            <CloseIcon />
          </IconButton>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className={clsx(classes.fileWrap, classes.fileMargin)}>
      <Chip label={name.name} onClick={() => window.open(url, '_blank')} onDelete={state.isEdit ? onDelete : undefined} />
    </div>
  );
};

const RenderChip = (props) => {
  const { deleteImage, file, valueKey } = props;

  const [formState, setFormState] = props.formState;

  const onChange = (e) => {
    set(formState, `${valueKey}.name`, e.target.value);
    setFormState({ ...formState });
  };

  useEffect(() => {
    onChange({ target: { value: get(file, 'file.name') } });
  }, []);

  return (
    <div key={file.id} style={{ marginRight: 5 }} onClick={stopProp}>
      <TextField
        value={get(formState, `${valueKey}.name`, '')}
        onChange={onChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={deleteImage} size="large">
                <Icon>delete</Icon>
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <div>{get(file, 'file.path', '')}</div>
    </div>
  );
};

export const FileUpload = (props) => {
  const { doc, update, userCanEdit } = props;
  const docs = Array.isArray(doc) ? doc : [doc || {}];

  const hasPerm = userCanEdit;
  const formRef = useRef();

  const [state, setState] = useState({
    isEdit: false,
    canEdit: hasPerm,
    showUpload: hasPerm,
    canSubmit: false,
    loading: false,
    error: undefined,
  });

  const [docsChanged, setDocsChanged] = useState({});
  const [formState, setFormState] = useState({});

  const [confirm, setConfirm] = useState();

  const classes = useStyles();
  const { isMobileXS } = useScreen();
  const fileType = props.fileType || get(first(docs), 'documentableType', '').replace('library_', '');

  const showClass = get(props, 'showClass', false);

  const supersets = useRetrieveData(
    { url: '/systems' },
    {
      build: ['systems'],
      waitFor: showClass,
      transformData: (d) => {
        return d.map((item) => ({ ...item, value: item.id, title: item.name }));
      },
    }
  );

  const classifications = useRetrieveData(
    { url: '/classifications' },
    {
      build: ['classifications'],
      waitFor: showClass,
      transformData: (d) => {
        return d.map((item) => ({ ...item, value: item.id, title: item.name }));
      },
    }
  );

  const FileSelect = (obj = {}) => {
    return get(obj, fileType);
  };

  const hasFiles = !!size(docs) && get(first(docs), 'id') !== FileSelect({ file: FILEADD.id, video: VIDEOADD.id });

  const toggleEdit = () => {
    setState((s) => ({ ...s, isEdit: !s.isEdit }));
  };

  const closeconfirm = () => {
    setConfirm();
  };

  const deleteIds = async (ids = []) => {
    try {
      setState((s) => ({ ...s, loading: true }));
      await awaitAll(
        ids.map((id) => () => async () => {
          await sendData({
            url: `/edition-documents/${id}`,
            method: 'delete',
          });
        })
      );
      await update();
      setState((s) => ({ ...s, loading: false }));
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err.message }));
    }
  };

  const deleteAll = () => {
    setConfirm({
      title: 'Delete All?',
      content: 'Are you sure you want to delete all files for this document?',
      onOk: () => {
        closeconfirm();
        deleteIds(docs.map((d) => d.id));
      },
      onCancel: closeconfirm,
    });
  };

  const onDeleteFile = (document) => {
    setConfirm({
      title: 'Delete File?',
      content: 'Are you sure you want to delete this file?',
      onOk: async () => {
        closeconfirm();
        deleteIds([document.id]);
      },
      onCancel: closeconfirm,
    });
  };

  const formSubmit = async (values) => {
    try {
      setState((s) => ({ ...s, loading: true, error: undefined }));

      const items = [...get(values, 'links', []), ...get(values, 'files', []), ...Object.keys(docsChanged).map((k) => docsChanged[k])];

      await awaitAll(
        items.map((_file) => () => async () => {
          try {
            const isLink = typeof _file === 'string';
            const file = isLink ? _file : get(_file, 'file');

            const id = get(_file, 'id');
            const hasId = id && id.indexOf('_file_') > -1 ? undefined : id;

            const attributes = {
              classification_id: get(values, 'classification_id', get(doc, '0.classificationId')).toString(),
              edition_id: get(props, 'editionId', get(doc, '0.editionId')).toString(),
              url: isLink ? file : undefined,
              name: isLink ? file : get(_file, 'name', get(_file, 'file.name', '')),
              files: isLink || hasId ? undefined : [file],
            };

            const relationships = {};

            const TYPE = FileSelect({ file: 'library-files', video: 'library-videos' });

            const data = {
              data: {
                id: hasId,
                type: TYPE,
                attributes,
                relationships,
              },
            };

            if (!isLink && !hasId) {
              await UploadFiles(data, { attributeType: 'files', isImage: false, collection_name: TYPE });
            }

            await sendData({
              url: `/${TYPE}${hasId ? `/${hasId}` : ''}`,
              method: hasId ? 'patch' : 'post',
              data,
            });
          } catch (err) {
            setState((s) => ({ ...s, error: err.message }));
          }
        }),
        { queueSize: 5 }
      );

      await update();
      setState((s) => ({ ...s, isEdit: false, loading: false }));
      setDocsChanged({});
      get(formRef, 'current.clearState', () => 0)();
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: `${err.message}. Please try again.` }));
    }
  };

  const onDocChange = (d) => {
    setDocsChanged({
      ...docsChanged,
      [d.id]: d,
    });
  };

  const message = (() => {
    return hasFiles ? '' : hasPerm ? FileSelect({ file: 'Upload a file to get started!', video: 'Upload a video to get started!' }) : 'No files here yet';
  })();

  const data = {};

  return (
    <div className={classes.container}>
      {state.canEdit && !state.loading && hasFiles && (
        <Button color={'secondary'} variant="contained" onClick={toggleEdit}>
          <Icon>{state.isEdit ? 'close' : 'edit'}</Icon>
          {'Edit'}
        </Button>
      )}

      {state.isEdit && !state.loading && (
        <Button color={'primary'} className={classes.deleteButton} variant="contained" onClick={deleteAll}>
          <Icon>delete</Icon>
          {'Delete All'}
        </Button>
      )}

      {state.error && !state.loading && (
        <Alert
          severity="error"
          action={
            <IconButton aria-label="close" color="inherit" size="small" onClick={() => setState((s) => ({ ...s, error: undefined }))}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {state.error}
        </Alert>
      )}

      {!state.loading && (
        <div className={classes.formStyle}>
          {(docs || [])
            .filter((f) => get(f, 'document.file') || get(f, 'document.url'))
            .sort((a, b) => (isVideoFile(get(a, 'document.file')) ? -1 : isVideoFile(get(b, 'document.file')) ? 1 : 0))
            .map((d) => (
              <RenderFile {...d} state={state} onDeleteFile={onDeleteFile} onDocChange={onDocChange} />
            ))}
        </div>
      )}

      {(state.isEdit || !hasFiles) && (
        <Form
          ref={formRef}
          loading={state.loading}
          showAllScreens={true}
          formStyle={classes.formStyle}
          onSubmit={formSubmit}
          canSubmit={
            (!!size(get(formState, 'files', [])) || !!size(get(formState, 'links', [])) || !!_size(docsChanged)) && state.showUpload && (showClass ? !!get(formState, 'classification_id') : true)
          }
          onFormState={(fs) => setFormState(fs)}
          submitText={
            <>
              <Icon style={{ marginRight: 5 }}>{'cloud_upload'}</Icon>Upload
            </>
          }
          screens={[
            {
              title: message,
              titleStyle: classes.titleStyle,
              screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
              screenContainer: {
                props: {
                  className: classes.screenWidth,
                },
              },
              inputs: [
                ...(state.canEdit && showClass
                  ? [
                      {
                        id: 'system',
                        type: 'Select',
                        data: () => supersets.data,
                        defaultValue: () => get(data, 'classification.system.id', 0),
                        props: {
                          label: 'System',
                          placeholder: 'Choose a System',
                        },
                      },
                      {
                        id: 'classification_id',
                        type: 'Select',
                        data: () => sortItemsAlphabetically(classifications.data.filter((f) => get(f, 'system.id') == get(formState, 'system'))),
                        defaultValue: () => get(data, 'classificationId', 0),
                        props: {
                          label: 'Classification',
                          placeholder: 'Choose Classification',
                          disabled: !get(formState, 'system'),
                          helperText: 'Why do we require a classification? This is the standardized data-point that organizes content together and allows the site to collate data more effectively.',
                        },
                      },
                    ]
                  : []),
                state.canEdit && {
                  id: 'files',
                  type: 'DropArea',
                  props: {
                    disabled: !state.showUpload,
                    label: FileSelect({ file: 'Files', video: 'Videos' }),
                    accept: FileSelect({ file: null, video: possiblevideos.map((v) => `video/${(v.mime ? v.mime : v).replace('.', '')}`) }),
                    renderContent: () => <>{`Drop ${FileSelect({ file: 'Files', video: 'Videos' })} here. Or click here.`}</>,
                    _renderFile: RenderChip,
                    fullWidth: true,
                    filesLimit: 100,
                    useChipsForPreview: true,
                    dropzoneClass: classes.dropZone,
                  },
                },
                state.canEdit &&
                  fileType !== 'video' && {
                    id: 'links',
                    type: 'ChipAdd',
                    defaultValue: () => [],
                    props: {
                      label: 'Add a Link',
                      placeholder: 'https://google.com',
                    },
                  },
              ].filter((f) => f),
            },
          ]}
        />
      )}

      <Confirm {...confirm} />
    </div>
  );
};
